import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { getDefaultInquiryURL } from '../../utils/whatsappGenerator';
import { businessInfo } from '../../data/mockData';
import { actionClasses, surfaceClasses } from '../../config/designSystem';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isPastHero, setIsPastHero] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/build', label: 'Build Your Bouquet' },
    { path: '/delivery', label: 'Delivery & Payment' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';
  const isCompact = isHomePage && isScrolled;
  const isLightMode = !isHomePage || isPastHero;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 36);
      if (!isHomePage) {
        setIsPastHero(true);
        return;
      }

      const hero = document.querySelector('.flowberry-cinematic-hero');
      setIsPastHero(hero ? hero.getBoundingClientRect().bottom <= 96 : window.scrollY > 640);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isHomePage, location.pathname]);

  return (
    <header className={`${surfaceClasses.header} ${isHomePage ? 'fixed inset-x-0 top-0' : 'sticky top-0 bg-gradient-to-b from-[#F7F2EE] to-[#FFF8F5]'}`}>
      {/* Top bar with hours */}
      <div className="hidden md:block">
        <div className={`max-w-7xl mx-auto flex items-center justify-end gap-4 overflow-hidden px-4 text-[11px] font-medium uppercase tracking-[0.16em] [transition:all_0.35s_ease] sm:px-6 lg:px-8 ${
          isLightMode ? 'text-[#3A2A2A]/70 drop-shadow-none' : 'text-white/78 drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]'
        } ${
          isCompact || isLightMode ? 'max-h-0 -translate-y-2 pt-0 opacity-0' : 'max-h-8 translate-y-0 pt-3 opacity-100'
        }`}>
          <span className="flex items-center gap-1.5">
            <Clock className={`w-3.5 h-3.5 ${isLightMode ? 'text-[#FB7185]' : 'text-rose-100'}`} />
            {businessInfo.workingHours}
          </span>
          <span className="hidden sm:inline text-rose-100/75">•</span>
          <span className={`hidden sm:inline ${isLightMode ? 'text-[#3A2A2A]/65' : 'text-white/72'}`}>Fast WhatsApp response</span>
        </div>
      </div>
      
      <div className="max-w-[1380px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`my-3 flex h-14 items-center justify-between rounded-full px-3 backdrop-blur-[20px] [transition:all_0.35s_ease] md:h-16 md:px-4 ${
          isLightMode
            ? 'mx-auto max-w-[1282px] border border-[#FB7185]/15 bg-white/[0.85] shadow-[0_10px_40px_rgba(0,0,0,0.08)]'
            : isCompact
            ? 'mx-auto max-w-[1282px] border border-[#F5BCB1]/35 bg-white/[0.16] shadow-[0_0_32px_rgba(245,188,177,0.32),0_18px_60px_rgba(27,18,23,0.22)]'
            : 'border border-white/[0.22] bg-white/[0.12] shadow-[0_18px_55px_rgba(27,18,23,0.18)]'
        }`}>
          {/* Logo */}
          <Link to="/" className="flex h-[72px] min-w-[220px] shrink-0 items-center justify-center overflow-visible">
            <img 
              src="/images/logon.png?v=3" 
              alt="FlowBerry" 
              data-navbar-logo="true"
              className="h-[72px] w-auto max-w-none object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-2" style={{display: 'none'}}>
              <span className={`font-serif text-xl md:text-2xl font-semibold ${isLightMode ? 'text-[#3A2A2A]' : 'text-white'}`}>FlowBerry</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative py-2 text-xs font-semibold uppercase tracking-[0.16em] [transition:all_0.35s_ease] after:absolute after:-bottom-0.5 after:left-0 after:h-px after:transition-all after:duration-200 hover:after:w-full ${
                  isLightMode
                    ? `drop-shadow-none after:bg-[#FB7185] hover:text-[#FB7185] ${isActive(link.path) ? 'text-[#FB7185] after:w-full' : 'text-[#3A2A2A] after:w-0'}`
                    : `drop-shadow-[0_2px_10px_rgba(0,0,0,0.25)] after:bg-rose-100 hover:text-white ${isActive(link.path) ? 'text-white after:w-full' : 'text-white/74 after:w-0'}`
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <a 
              href={getDefaultInquiryURL()}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 text-sm font-medium [transition:all_0.35s_ease] ${
                isLightMode
                  ? 'text-[#3A2A2A] drop-shadow-none hover:text-[#FB7185]'
                  : 'text-white/82 drop-shadow-[0_2px_10px_rgba(0,0,0,0.25)] hover:text-white'
              }`}
            >
              <MessageCircle className={`w-4 h-4 ${isLightMode ? 'text-[#FB7185]' : 'text-rose-100'}`} />
              WhatsApp
            </a>
            <Link to="/build">
              <Button className={`${actionClasses.primary} rounded-full px-5 shadow-[0_12px_34px_rgba(245,188,177,0.32)]`}>
                Build Your Bouquet
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden rounded-full p-2 shadow-[0_8px_24px_rgba(27,18,23,0.16)] backdrop-blur-md [transition:all_0.35s_ease] ${
              isLightMode
                ? 'border border-[#FB7185]/15 bg-white/70 text-[#3A2A2A] hover:bg-white/90 hover:text-[#FB7185]'
                : 'border border-white/[0.22] bg-white/[0.14] text-white hover:bg-white/[0.22]'
            }`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className={`lg:hidden mb-3 rounded-2xl px-3 py-4 backdrop-blur-[20px] [transition:all_0.35s_ease] ${
            isLightMode
              ? 'border border-[#FB7185]/15 bg-white/[0.85] shadow-[0_10px_40px_rgba(0,0,0,0.08)]'
              : 'border border-white/[0.20] bg-white/[0.16] shadow-[0_18px_55px_rgba(27,18,23,0.18)]'
          }`}>
            {/* Mobile hours */}
            <div className={`px-4 py-2 mb-2 text-xs font-medium flex items-center gap-1.5 ${isLightMode ? 'text-[#3A2A2A]/70' : 'text-white/72'}`}>
              <Clock className={`w-3.5 h-3.5 ${isLightMode ? 'text-[#FB7185]' : 'text-rose-100'}`} />
              {businessInfo.workingHours}
            </div>
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isLightMode
                      ? isActive(link.path)
                        ? 'bg-[#FB7185]/10 text-[#FB7185]'
                        : 'text-[#3A2A2A] hover:bg-[#FB7185]/8 hover:text-[#FB7185]'
                      : isActive(link.path)
                      ? 'bg-white/[0.16] text-white'
                      : 'text-white/78 hover:bg-white/[0.12] hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={getDefaultInquiryURL()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                  isLightMode
                    ? 'text-[#3A2A2A] hover:bg-[#FB7185]/8 hover:text-[#FB7185]'
                    : 'text-white/78 hover:bg-white/[0.12] hover:text-white'
                }`}
              >
                <MessageCircle className={`w-4 h-4 ${isLightMode ? 'text-[#FB7185]' : 'text-rose-100'}`} />
                WhatsApp
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
